import { useState, useEffect } from "react";
import {
  DialogContent,
  Button,
  TextField,
  Slider,
  Typography,
  Box,
  Stack,
  Skeleton,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
} from "@mui/material";
import wellnessIllustration from "@assets/images/WeeklyIllustration.svg";
import ReactModal from "react-modal";
import { getAllFormFields, updateWeeklyCheckIn } from "api/preferences";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { WeeklyCheckIn } from "@api/weeklyTracker";

export const WeeklyCheckInModal = ({ open, setOpen }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    currentWeight: "",
    targetWeight: "",
    goalBodyFat: "",
    currentBodyFatPercentage: "",
    goalWaistCircumference: "",
    currentWaistCircumference: "",
    sleepQuality: 5,
    energyLevel: 5,
    stressLevel: 5,
    motivationLevel: 5,
    targetWeightUnit: "kg",
    currentWeightUnit: "kg",
    goalWeightUnit: "kg",
  });
  const [firstLoad, setFirstLoad] = useState(true);
  const [errors, setErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [deviceData, setDeviceData] = useState({
    currentWeightFromDevice: "",
    currentBodyFatFromDevice: "",
  });

  // Conversion functions
  const kgToLbs = (kg) => (kg * 2.20462).toFixed(2);
  const convertWeight = (value, fromUnit, toUnit) => {
    if (!value) return value;
    if (fromUnit === toUnit) return value;

    // kg ↔ lbs conversion
    if (fromUnit === "kg" && toUnit === "lbs")
      return (value * 2.20462).toFixed(1);
    if (fromUnit === "lbs" && toUnit === "kg")
      return (value / 2.20462).toFixed(1);

    return value;
  };

  // --- Start of Validation Logic ---

  // Define fields that require specific validation
  const FieldsToValidate = {
    currentWeight: 3,
    currentBodyFatPercentage: 2,
    currentWaistCircumference: 3,
  };

  // The fields that are user inputs and need validation
  const fieldsToCheck = [
    "currentWeight",
    "currentBodyFatPercentage",
    "currentWaistCircumference",
  ];

  const validateInput = (key, value) => {
    // 1. Live Validation: Allow empty string to prevent "required" flashing while typing
    if (value === "" || value === null || value === undefined) {
      return null;
    }

    const numericValue = Number(value);

    // 2. Check for valid number
    if (isNaN(numericValue)) {
      return "Please enter a valid number.";
    }

    // 3. Check for negative numbers
    if (numericValue < 0) {
      return "Value cannot be negative.";
    }

    // 4. Check for values less than 1 (for weight and waist)
    if (numericValue < 1 && key !== "currentBodyFatPercentage") {
      return "Value must be greater than 0.";
    }

    // 5. Specific validation for 'currentWeight'
    if (key === "currentWeight" && numericValue > 1000) {
      return "Weight cannot exceed 1000 kg.";
    }

    // 6. Specific validation for 'currentBodyFatPercentage' (must be between 1 and 60)
    if (
      key === "currentBodyFatPercentage" &&
      (numericValue < 1 || numericValue > 60)
    ) {
      return "Body fat percentage must be between 1 and 60.";
    }

    // 7. Specific validation for 'currentWaistCircumference'
    if (key === "currentWaistCircumference" && numericValue > 200) {
      return "Waist circumference cannot exceed 200 cm.";
    }

    return null;
  };

  const validateAllFields = () => {
    const newErrors = {};

    fieldsToCheck.forEach((field) => {
      const value = formData[field];

      // Format field name for readable error messages
      const formattedField = field
        .replace(/([A-Z])/g, " $1") // insert space before capital letters
        .replace(/^./, (str) => str.toUpperCase()); // capitalize first letter

      // Check required fields
      if (value === "" || value === null || value === undefined) {
        newErrors[field] = `The ${formattedField} is required.`;
        return;
      }

      // Run custom validation rules
      const error = validateInput(field, value);
      if (error) {
        newErrors[field] = error;
        const validateAllFields = () => {
          const newErrors = {};
          fieldsToCheck.forEach((field) => {
            const value = formData[field];

            // Explicitly check for 'required' error on save
            if (value === "" || value === null || value === undefined) {
              newErrors[field] = `The ${field} is required.`;
              return;
            }

            // Run other validation rules
            const error = validateInput(field, value);
            if (error) {
              newErrors[field] = error;
            }
          });

          setErrors(newErrors);
          // Return true if there are NO errors
          return Object.keys(newErrors).length === 0;
        };
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --- End of Validation Logic ---

  // MODIFIED: handleInputChange now validates in real-time
  const handleInputChange = (field, value) => {
    // Update state with the string value from input
    setFormData((prev) => ({ ...prev, [field]: value }));

    // Run validation and update errors immediately
    const error = validateInput(field, value);
    setErrors((prev) => ({ ...prev, [field]: error }));
  };

  const handleWeightUnitChange = (field, newUnit) => {
    setFormData((prev) => {
      if (field === "currentWeightUnit") {
        // Use the string value from state before conversion
        const convertedWeight = convertWeight(
          prev.currentWeight,
          prev.currentWeightUnit,
          newUnit
        );
        return {
          ...prev,
          currentWeight: convertedWeight,
          currentWeightUnit: newUnit,
        };
      }

      return { ...prev, [field]: newUnit };
    });
  };

  const handleSliderChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };
  const handleSave = async () => {
    if (!validateAllFields()) return;

    setIsSaving(true);

    // Create a copy of formData for sending
    let dataToSend = { ...formData };

    // Convert current weight to kg if user selected lbs
    if (formData.currentWeightUnit === "lbs") {
      dataToSend.currentWeight = convertWeight(
        formData.currentWeight,
        "lbs",
        "kg"
      );
      dataToSend.currentWeightUnit = "kg"; // store unit in kg
    }

    // You can also ensure targetWeight is stored in kg for consistency
    if (formData.targetWeightUnit === "lbs") {
      dataToSend.targetWeight = convertWeight(
        formData.targetWeight,
        "lbs",
        "kg"
      );
      dataToSend.targetWeightUnit = "kg";
    }

    // Add other metadata fields
    const finalData = {
      ...dataToSend,
      weight: dataToSend.currentWeight,
      weightUnit: dataToSend.currentWeightUnit,
      waistCircumference: dataToSend.currentWaistCircumference,
      bodyFatPercentage: dataToSend.currentBodyFatPercentage,
      notes: "information submitting",
      tracking_date: new Date(),
    };

    try {
      await updateWeeklyCheckIn(finalData);
      setOpen(false);
      toast.info(t("toast.informationSubmit"));
    } catch (e) {
      toast.error(t("toast.failedSubmit"));
      console.error("Failed to save check-in:", e);
    } finally {
      setIsSaving(false);
    }
  };

  const getFormFields = async () => {
    try {
      const data = await WeeklyCheckIn();
      const tags = data;
      setFormData((prev) => ({
        ...prev,
        // Fetching GOAL values from preferences
        goalBodyFat: tags?.goalBodyFat || "",
        targetWeight: tags?.targetWeight || "",
        goalWaistCircumference: tags?.goalWaistCircumference || "",
        currentWeight: tags?.currentWeight || "",
        currentBodyFatPercentage: tags?.currentBodyFatPercentage || "",
        currentWaistCircumference: tags?.currentWaistCircumference || "",

        targetWeightUnit: tags?.targetWeightUnit?.toLowerCase() || "kg",
        currentWeightUnit: tags?.targetWeightUnit?.toLowerCase() || "kg",
      }));
      setDeviceData({
        currentWeightFromDevice: tags?.currentWeight || "",
        currentBodyFatFromDevice: tags?.currentBodyFatPercentage || "",
      });
    } catch (error) {
      console.error(
        "Error fetching form fields:",
        error.response ? error.response.data : error.message
      );
    } finally {
      setFirstLoad(false);
    }
  };

  useEffect(() => {
    if (open) {
      setErrors({}); // Clear errors when modal opens
      getFormFields();
    }
  }, [open]);

  // Get display value for goal weight based on selected unit
  const getGoalWeightDisplayValue = () => {
    if (!formData.targetWeight) return "";

    // Convert the targetWeight from its stored unit (targetWeightUnit)
    // to the display unit (goalWeightUnit).
    const value = convertWeight(
      formData.targetWeight,
      formData.targetWeightUnit,
      formData.targetWeightUnit
    );

    return value;
  };

  // Skeleton Loader Component (omitted for brevity, assume it's fine)
  const FormSkeleton = () => (
    <Stack spacing={2.5}>
      {/* Input Field Skeletons */}
      {[1, 2, 3, 4, 5, 6].map((item) => (
        <Box key={item}>
          <Skeleton
            variant="text"
            width="60%"
            height={24}
            sx={{ mb: 1, borderRadius: "4px" }}
          />
          <Skeleton
            variant="rectangular"
            width="100%"
            height={40}
            sx={{ borderRadius: "8px" }}
          />
        </Box>
      ))}

      {/* Slider Skeletons */}
      {[1, 2, 3, 4].map((item) => (
        <Box key={item}>
          <Skeleton
            variant="text"
            width="50%"
            height={24}
            sx={{ mb: 1, borderRadius: "4px" }}
          />
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Skeleton
              variant="rectangular"
              width="100%"
              height={16}
              sx={{ borderRadius: "8px", flex: 1 }}
            />
            <Skeleton variant="text" width={32} height={24} />
          </Box>
        </Box>
      ))}

      {/* Button Skeletons */}
      <Box
        sx={{
          display: "flex",
          gap: 1.5,
          mt: 4,
          justifyContent: "center",
        }}
      >
        <Skeleton
          variant="rectangular"
          width={100}
          height={40}
          sx={{ borderRadius: "5px" }}
        />
        <Skeleton
          variant="rectangular"
          width={140}
          height={40}
          sx={{ borderRadius: "5px" }}
        />
      </Box>
    </Stack>
  );

  return (
    <ReactModal
      isOpen={open}
      // onRequestClose={() => setOpen(false)}
      className="relative w-[100%] max-w-[90%] md:max-w-[490px] h-screen max-h-[80%] bg-transparent font-[Lato] outline-none overflow-visible"
      overlayClassName="fixed inset-0 flex items-center justify-center backdrop-blur-sm bg-black/50 z-50"
    >
      <div className="relative flex flex-col items-center">
        {/* Wellness Illustration - Outside the modal */}
        {firstLoad ? (
          <Skeleton
            variant="circular"
            width={160}
            height={160}
            className="mb-[-70px] z-10"
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.2)",
            }}
          />
        ) : (
          <img
            src={wellnessIllustration}
            alt="Wellness illustration"
            className="w-[160px] h-[160px] object-cover mb-[-70px] z-10 rounded-full"
          />
        )}

        {/* Modal Content */}
        <div className="w-full bg-white shadow-xl border border-gray-200 rounded-[24px] overflow-y-auto max-h-[calc(80vh-80px)] pt-[20px]">
          <DialogContent sx={{ px: 3, pb: 3, pt: 6 }}>
            {firstLoad ? (
              <>
                {/* Header Skeleton */}
                <Box sx={{ mb: 3, textAlign: "center" }}>
                  <Skeleton
                    variant="text"
                    width="70%"
                    height={32}
                    sx={{ mx: "auto", mb: 1, borderRadius: "4px" }}
                  />
                  <Skeleton
                    variant="text"
                    width="90%"
                    height={20}
                    sx={{ mx: "auto", borderRadius: "4px" }}
                  />
                  <Skeleton
                    variant="text"
                    width="80%"
                    height={20}
                    sx={{ mx: "auto", borderRadius: "4px" }}
                  />
                </Box>

                {/* Form Skeleton */}
                <FormSkeleton />
              </>
            ) : (
              <>
                <Box sx={{ mb: 3, textAlign: "center" }}>
                  <h1 className="text-[#46467D] font-[Lato] text-[20px] font-[700]">
                    {t("Weekly Check-In")}
                  </h1>
                  <Typography variant="body2" color="#7F7F7F" sx={{ mt: 1 }}>
                    {t(
                      "Log your progress and update your weekly stats to stay on track with your goals."
                    )}
                  </Typography>
                </Box>

                <Stack spacing={2.5}>
                  {/* 1. Current Weight Input */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      {t("1. Current Weight")}
                    </p>
                    <div className="flex gap-2">
                      <TextField
                        type="number"
                        placeholder={t("Current Weight")}
                        value={formData.currentWeight || ""}
                        onChange={(e) =>
                          handleInputChange("currentWeight", e.target.value)
                        }
                        error={!!errors.currentWeight}
                        helperText={errors.currentWeight}
                        fullWidth
                        size="small"
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                          },
                        }}
                        inputProps={{
                          step: "0.1",
                        }}
                      />
                      <FormControl sx={{ minWidth: 80 }}>
                        <Select
                          value={formData.currentWeightUnit}
                          onChange={(e) =>
                            handleWeightUnitChange(
                              "currentWeightUnit",
                              e.target.value
                            )
                          }
                          size="small"
                          sx={{
                            borderRadius: "8px",
                            height: "42px",
                            "& .MuiOutlinedInput-notchedOutline": {
                              borderColor: "#D2D2D2",
                            },
                          }}
                        >
                          <MenuItem value="kg">kg</MenuItem>
                          <MenuItem value="lbs">lbs</MenuItem>
                        </Select>
                      </FormControl>
                    </div>
                    {!!deviceData?.currentWeightFromDevice && (
                      <Typography
                        variant="caption"
                        color="#7F7F7F"
                        sx={{ mt: 0.5 }}
                      >
                        {t("Weight fetch from device")}:{" "}
                        {deviceData.currentWeightFromDevice}{" "}
                        {formData.targetWeightUnit}
                      </Typography>
                    )}
                  </Box>

                  {/* 2. Goal Weight Input (Read-only) */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      {t("2. Goal Weight")}
                    </p>
                    <div className="flex gap-2 relative">
                      <TextField
                        type="number"
                        placeholder={t("Goal Weight")}
                        value={getGoalWeightDisplayValue()}
                        disabled
                        fullWidth
                        size="small"
                        sx={{
                          flex: 1,
                          "& .MuiOutlinedInput-root": {
                            borderRadius: "8px",
                            backgroundColor: "#EEEEEE", // Disabled color
                          },
                        }}
                      />
                      <div className="absolute right-4 h-[42px] leading-[42px] text-[#7F7F7F] pointer-events-none">
                        {formData.targetWeightUnit}
                      </div>
                    </div>
                  </Box>

                  {/* 3. Current Body Fat % */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      {t("3. Current Body Fat %")}
                    </p>
                    <TextField
                      type="number"
                      placeholder={t("Current Body Fat %")}
                      value={formData.currentBodyFatPercentage || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "currentBodyFatPercentage",
                          e.target.value
                        )
                      }
                      error={!!errors.currentBodyFatPercentage}
                      helperText={errors.currentBodyFatPercentage}
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                      inputProps={{
                        step: "0.1",
                      }}
                    />
                    {!!deviceData?.currentBodyFatFromDevice && (
                      <Typography
                        variant="caption"
                        color="#7F7F7F"
                        sx={{ mt: 0.5 }}
                      >
                        {t("Body Fat % fetch from device")}:{" "}
                        {deviceData.currentBodyFatFromDevice}{" "}%
                      </Typography>
                    )}
                  </Box>

                  {/* 4. Body Fat % Goal (Read-only) */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      {t("4. Body Fat % Goal")}
                    </p>
                    <TextField
                      type="number"
                      placeholder={t("Body Fat % Goal")}
                      value={formData.goalBodyFat}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#EEEEEE",
                        },
                      }}
                    />
                  </Box>

                  {/* 5. Current Waist Measurement (cm) */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      5. {t("Current Waist Measurement")}
                    </p>
                    <TextField
                      type="number"
                      placeholder={t("Current Waist Measurement")}
                      value={formData.currentWaistCircumference || ""}
                      onChange={(e) =>
                        handleInputChange(
                          "currentWaistCircumference",
                          e.target.value
                        )
                      }
                      error={!!errors.currentWaistCircumference}
                      helperText={errors.currentWaistCircumference}
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                      inputProps={{
                        step: "0.1",
                      }}
                    />
                  </Box>

                  {/* 6. Goal Waist Measurement (cm) (Read-only) */}
                  <Box>
                    <p className="text-[16px] font-[Lato] mb-1">
                      6. {t("Goal Waist Measurement")}
                    </p>
                    <TextField
                      type="number"
                      placeholder={t("Goal Waist Measurement")}
                      value={formData.goalWaistCircumference}
                      disabled
                      fullWidth
                      size="small"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                          backgroundColor: "#EEEEEE",
                        },
                      }}
                    />
                  </Box>

                  {/* Sliders */}
                  {[
                    {
                      label: t("Sleep Quality"),
                      field: "sleepQuality",
                      num: 7,
                    },
                    { label: t("Energy Level"), field: "energyLevel", num: 8 },
                    { label: t("Stress Level"), field: "stressLevel", num: 9 },
                    {
                      label: t("Motivation Level"),
                      field: "motivationLevel",
                      num: 10,
                    },
                  ].map(({ label, field, num }) => (
                    <Box key={field}>
                      <p className="text-[16px] font-[Lato] mb-1">
                        {num}. {label} (1-10)
                      </p>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 2 }}
                      >
                        <Slider
                          value={formData[field]}
                          onChange={(e, value) =>
                            handleSliderChange(field, value)
                          }
                          min={1}
                          max={10}
                          step={1}
                          valueLabelDisplay="auto"
                          sx={{
                            border: "none",
                            height: "16px",
                            flex: 1,
                            "& .MuiSlider-thumb": {
                              borderRadius: "50%",
                              background:
                                "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                              border: "3px solid white",
                              boxShadow: "0 0 4px rgba(0,0,0,0.2)",
                            },
                            "& .MuiSlider-track": {
                              background:
                                "linear-gradient(150deg, #0771EF 12.89%, #15AAFC 85.22%)",
                            },
                            "& .MuiSlider-rail": {
                              backgroundColor: "#d0d0d0",
                            },
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: 500,
                            width: 32,
                            textAlign: "center",
                          }}
                        >
                          {formData[field]}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                </Stack>

                {/* Action Buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1.5,
                    mt: 4,
                    justifyContent: "center",
                  }}
                >
                  <button
                    onClick={handleSave}
                    className="text-[#fff] font-[Lato] text-[12px] rounded-[5px] px-5 py-2 shadow-md bg-[linear-gradient(150deg,_#0771EF_12.89%,_#15AAFC_85.22%)] font-[600] flex items-center justify-center"
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <CircularProgress size={20} color="inherit" />
                    ) : (
                      t("Save & Update")
                    )}
                  </button>
                </Box>
              </>
            )}
          </DialogContent>
        </div>
      </div>
    </ReactModal>
  );
};